import React from "react";
import PropTypes from "prop-types";
import { InputText, Label } from "../../../../components/Inputs";
import { validateEmail } from "../../../../components/Utils";

const getState = (model = {}) => {
  return {
    name: model.name || "",
    email: model.email || "",
    error: {
      name: null,
      email: null,
    },
  };
};

function AdHocNewsRecipentForm({
  forwardedRef,
  className = "",
  model = {
    name: "",
    email: "",
  },
  namePrefix = "",
  disabled,
  hideLabels,
  autoFocus,
}) {
  const [state, setState] = React.useState(getState(model));

  const validate = React.useCallback(() => {
    const { error, ...newModel } = state;
    let isValid = true;
    // name
    if (newModel.name === "") {
      error.name = true;
      isValid = false;
    }
    // email
    if (!validateEmail(newModel.email)) {
      error.email = "E-Mail ist ungültig (Umlaute/Sonderzeichen entfernen)";
      isValid = false;
    }
    if (!isValid) {
      setState((prev) => ({ ...prev, error }));
      return null;
    }
    const { id, index } = model;
    return { id, index, ...newModel };
  }, [state, model]);

  const getModel = React.useCallback(
    (noValidate) => validate(noValidate),
    [validate],
  );
  const reset = React.useCallback(
    () => setState(getState({ model: {} })),
    [getModel],
  );
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
    reset,
  }));

  const onChange = React.useCallback((key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
      error: {
        ...prev.error,
        [key]: value === "" ? true : null,
      },
    }));
  }, []);

  const errorName = state.error.name ? "error-group" : "";
  const errorEmail = state.error.email ? "error-group" : "";
  return (
    <div className={className}>
      <div className="w3-row neg-margin-lr">
        <div
          className={`w3-col s6 pad-lr pad-top form-group-item- ${errorName}`}
        >
          {!hideLabels && (
            <Label htmlFor={`${namePrefix}recipient_name`}>{`Name*`}</Label>
          )}
          <InputText
            id={`${namePrefix}recipient_name`}
            data-test-id={`${namePrefix}recipient_name`}
            className="w3-block w3-border"
            value={state.name}
            onChange={(event) => onChange("name", event.target.value)}
            disabled={disabled}
            autoFocus={autoFocus}
          />
        </div>
        <div
          className={`w3-col s6 pad-lr pad-top form-group-item- ${errorEmail}`}
        >
          {!hideLabels && (
            <Label htmlFor={`${namePrefix}recipient_email`}>{`E-Mail*`}</Label>
          )}
          <InputText
            id={`${namePrefix}recipient_email`}
            data-test-id={`${namePrefix}recipient_email`}
            className="w3-block w3-border"
            value={state.email}
            onChange={(event) => onChange("email", event.target.value)}
            disabled={disabled}
          />
          {state.error.email && (
            <div className="pad-sm-top">{state.error.email}</div>
          )}
        </div>
      </div>
    </div>
  );
}

AdHocNewsRecipentForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.object,
  disabled: PropTypes.bool,
  hideLabels: PropTypes.bool,
  autoFocus: PropTypes.bool,
  namePrefix: PropTypes.string,
};

export default React.forwardRef((props, ref) => (
  <AdHocNewsRecipentForm {...props} forwardedRef={ref} />
));

export function InputValue({
  className = "",
  dataKey = "",
  value: pValue = "",
  validate: pValidate,
  disabled,
}) {
  const [state, setState] = React.useState({
    value: pValue || "",
    error: null,
  });

  const validate = () => {
    const { value } = state;
    let isValid = true;
    // value
    let error = false;
    if (!pValidate(value)) {
      error = true;
      isValid = false;
    }
    if (!isValid) {
      setState((prev) => ({ ...prev, error }));
      return null;
    }
    return value;
  };

  const getModel = React.useCallback(
    (noValidate) => validate(noValidate),
    [validate],
  );

  const reset = React.useCallback(
    () =>
      setState({
        value: value || "",
        error: null,
      }),
    [getModel],
  );

  onChange = React.useCallback(
    (value) => {
      setState({
        value,
        error: pValidate(value) ? true : null,
      });
    },
    [pValidate],
  );

  return (
    <div className={`${className} ${state.error ? "error-group" : ""}`}>
      <InputText
        id={dataKey}
        data-test-id={dataKey}
        className="w3-block w3-border"
        value={state.value}
        onChange={(event) => onChange(event.target.value)}
        readonly={disabled}
      />
    </div>
  );
}

InputValue.propTypes = {
  className: PropTypes.string,
  dataKey: PropTypes.string,
  value: PropTypes.string,
  validate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hideLabels: PropTypes.bool,
  autoFocus: PropTypes.bool,
};
